<script setup lang="ts">
import { Order, OrdersnapShot } from '@/@types/order'
import { PaymentHeader, PaymentTitle } from '@/components/shared/checkout/payment-header'
import { Button } from '@/components/ui/button'
import { Separator } from '@/components/ui/separator'
import { Link, usePage } from '@inertiajs/vue3'

import { SharedData } from '@/@types'
import { Coupon } from '@/@types/cart'
import { Badge } from '@/components/ui/badge'
import { formatPrice } from '@/helpers/current-price'
import Resume from '../cart/payment-methods/Resume.vue'
import PaymentStepper from './payment-header/PaymentStepper.vue'

interface OrderWithCoupon extends Order {
    coupon: Coupon
    order_snap_shot: OrdersnapShot
}
const { user } = usePage<SharedData>().props
const { order } = defineProps<{ order: OrderWithCoupon }>()
</script>

<template>
    <main class="container" v-if="order.transaction_detail">
        <PaymentHeader>
            <PaymentTitle>
                {{ $t('Payment received') }}
                <template #description>
                    {{ $t('Your order has been successfully placed.') }} <strong>#{{ order.id }}</strong
                    >.
                </template>
            </PaymentTitle>
            <PaymentStepper
                :steps="[
                    {
                        name: $t('Order placed'),
                        state: 'completed',
                    },
                    { name: $t('Payment received'), state: 'active' },
                ]"
            />
        </PaymentHeader>

        <section class="mt-6 flex flex-col gap-8 lg:flex-row">
            <div class="flex flex-1 flex-col items-center justify-center rounded-2xl border border-slate-200 bg-white px-6 py-8">
                <div class="mx-auto flex max-w-xl flex-col items-center justify-center">
                    <Badge
                        :variant="order.status"
                        class="rounded-sm border-0 before:size-1.5 before:rounded-full before:bg-yellow-400 before:content-['']"
                    >
                        {{ $t(order.status) }}
                    </Badge>

                    <h2 class="mt-4 mb-2.5 text-center font-sans text-2xl font-semibold">
                        {{ $t('Amount paid:') }} {{ formatPrice(order.transaction_detail.amount / 100) }}
                    </h2>

                    <p
                        class="text-center text-[#444]"
                        v-html="
                            $t(
                                `Thank you for purchasing from ILOS! A summary of your order has been sent to  with information about the tickets purchased and the invoice for your order.`,
                                { email: user?.email },
                            )
                        "
                    />
                </div>

                <Separator class="mx-auto my-8 max-w-[612px]" />

                <div class="mb-8 w-full rounded-sm bg-indigo-100 p-4 text-center text-sm font-medium text-indigo-800">
                    <p class="mx-auto max-w-2xl">
                        {{ $t('Within 24 business hours, our team will contact you to proceed with Billing.') }}
                    </p>
                </div>

                <Button :as="Link" href="?show_order_details=true">{{ $t('View order details') }}</Button>
            </div>
            <Resume :order="order" />
        </section>
    </main>
</template>
